import { formatAddressDisplay } from '@/features/applicant/utils/addressHelpers'
import type {
  AboutYourselfAddressEditFormData,
  AboutYourselfAlternateAddressFormData,
  AboutYourselfFormChangePayload,
  AboutYourselfPersonalFormData,
} from '../../types/tabs'
import type {
  AddressLike,
  AlternateAddressLike,
  PreviousAddressRow,
} from '../../types/utils'
import { normalizePreviousAddressRows } from '../../utils/previousAddressLogic'

export function isUsCountry(country: string | undefined | null): boolean {
  const c = String(country ?? "")
    .trim()
    .toUpperCase();
  return c === "US" || c === "USA";
}

export function aboutAddressTestId(
  addressType: string,
  addressIndex: number | undefined,
  slug: string,
  suffix: string,
): string {
  const block =
    addressType === "current_address"
      ? "current-address"
      : `previous-address-${addressIndex ?? 0}`;
  return `application-about-tab-${block}-${slug}-${suffix}`;
}

export {
  ABOUT_YOURSELF_REVEAL_INLINE_ERRORS_EVENT,
  ADDRESS_MAP_LINK_ROLES,
  ADDRESS_TYPES,
  ABOUT_YOURSELF_RELATIONSHIP_TYPES as RELATIONSHIP_TYPES,
  COMMUNICATION_PREFERENCES,
  EMAIL_VALIDATE_ROLES,
  US_STATE_OPTIONS,
} from '../../constants';

export const formatPhoneInput = (value: string): string => {
  const cleaned = value.replace(/\D/g, "");
  const limited = cleaned.slice(0, 11);

  if (limited.length === 0) return "";
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6)
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  if (limited.length <= 10)
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  return `+${limited.slice(0, 1)} (${limited.slice(1, 4)}) ${limited.slice(4, 7)}-${limited.slice(7)}`;
};

const MASKED_SSN_PATTERN = /^X{3}-X{2}-\d{4}$/i;

/** Angular `appSSNNumber` — formats as XXX-XX-XXXX while typing (dashes added automatically). */
export const formatSSNInput = (value: string): string => {
  const trimmed = value.trim();
  if (MASKED_SSN_PATTERN.test(trimmed)) return trimmed;

  const cleaned = value.replace(/\D/g, "").slice(0, 9);
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
};

/** Store digits only while typing; preserve masked edit value (XXX-XX-1234) until replaced. */
export const parseSSNInput = (value: string): string => {
  const trimmed = value.trim();
  if (MASKED_SSN_PATTERN.test(trimmed)) return trimmed;
  return value.replace(/\D/g, "").slice(0, 9);
};

export function createEmptyAlternateAddressForm(): AboutYourselfAlternateAddressFormData {
  return {
    ra_id: "",
    street_address: "",
    house_no: "",
    street: "",
    apartment: "",
    city: "",
    state: "",
    zip_code: "",
    country: "US",
    outside_us: false,
  };
}

/** Re-seed alternate rows with server `ra_id` after save+refetch (Angular `setAlternateAddress` parity). */
export function mapAlternateAddressesToForms(
  alternateAddresses: ReadonlyArray<AlternateAddressLike> | null | undefined,
): AboutYourselfAlternateAddressFormData[] {
  return (alternateAddresses ?? []).map((addr) => ({
    ra_id: addr.ra_id ?? "",
    street_address: formatAddressDisplay(addr),
    house_no: addr.house_no || "",
    street: addr.street || "",
    apartment: addr.apartment ?? "",
    city: addr.city || "",
    state: addr.state || "",
    zip_code: addr.zip_code || "",
    country: addr.country || "US",
    outside_us: addr.outside_us || false,
  }));
}

export function hasNonUSAddress(
  currentAddress: PreviousAddressRow | AddressLike | null | undefined,
  previousAddresses: Array<PreviousAddressRow | AddressLike>,
): boolean {
  const isNonUS = (addr: PreviousAddressRow | AddressLike | null | undefined) =>
    Boolean(
      addr?.outside_us ||
        (addr?.country && addr.country !== "US" && addr.country !== "USA"),
    );

  // Angular `validateAllUSAddress`: non-US checkbox only when current and all previous are outside US.
  if (!isNonUS(currentAddress)) return false;

  return !previousAddresses.some((addr) => addr && !isNonUS(addr));
}

/** Angular `setPreviousAddressPrefill()` — normalize API/import rows for AddressEditForm. */
export function mapApiAddressToPreviousAddressRow(
  addr: AddressLike | PreviousAddressRow,
): PreviousAddressRow {
  const country = addr.country || "US";
  const outsideUs =
    Boolean(addr.outside_us) ||
    (country !== "US" && country !== "USA");
  const addressData = { ...(addr.address_data ?? {}) };
  const street_address = String(
    addr.street_address ||
      formatAddressDisplay({
        house_no: addr.house_no,
        street: addr.street,
        street_type: (addr as { street_type?: string }).street_type,
        apartment: addr.apartment,
        city: addr.city,
        state: addr.state,
        zip_code: addr.zip_code,
        country,
      }),
  );

  return {
    ...addr,
    street_address,
    country,
    outside_us: outsideUs,
    address_type: addr.address_type || "RENT",
    address_data: addressData,
  } as PreviousAddressRow;
}

/** Flat edit-form rows + API rows → one shape for validation / previous-section rules. */
export function coalesceAddressLikeForRules(
  addressForm:
    | PreviousAddressRow
    | AboutYourselfAddressEditFormData
    | AddressLike
    | null
    | undefined,
  isCurrentAddress: boolean = true,
): AddressLike | null {
  if (!addressForm) return null
  return formatAboutYourselfAddressForApi(addressForm, isCurrentAddress)
}

export function formatAboutYourselfAddressForApi(
  addressForm: PreviousAddressRow | AboutYourselfAddressEditFormData,
  isCurrentAddress: boolean = true,
): AddressLike | null {
  if (!addressForm) return null;
  const existingAddressData =
    (addressForm.address_data as Record<string, unknown> | null | undefined) ??
    {};
  const addressType = addressForm.address_type || "RENT";
  const addressRecord = addressForm as Record<string, unknown>;
  const addressDataValue = (field: string): unknown =>
    Object.prototype.hasOwnProperty.call(addressRecord, field)
      ? addressRecord[field]
      : existingAddressData[field];

  const asPayloadField = (field: string): string | number | null | undefined => {
    const value = addressDataValue(field);
    if (value === null || value === undefined || value === "") return undefined;
    if (typeof value === "number") return value;
    return String(value);
  };

  const addressData: NonNullable<AddressLike["address_data"]> = {
    moveIn_date: String(asPayloadField("moveIn_date") ?? ""),
  };

  if (addressType === "OWN") {
    addressData.month_reside = asPayloadField("month_reside");
    if (isCurrentAddress) {
      addressData.rent_amount = String(asPayloadField("rent_amount") ?? "");
      addressData.moveOut_date = String(asPayloadField("moveOut_date") ?? "");
      addressData.currently_living =
        (addressForm.currently_living ?? existingAddressData.currently_living)
          ? "1"
          : "0";
    }
  } else {
    addressData.landlord_name = String(asPayloadField("landlord_name") ?? "");
    addressData.landlord_email = String(asPayloadField("landlord_email") ?? "");
    addressData.landlord_mobile_no = String(
      asPayloadField("landlord_mobile_no") ?? "",
    );
    const paidRentRaw = asPayloadField("paid_rent");
    if (paidRentRaw == null || paidRentRaw === "") {
      addressData.paid_rent = null;
    } else if (typeof paidRentRaw === "number") {
      addressData.paid_rent = paidRentRaw;
    } else {
      const parsed = Number(paidRentRaw);
      addressData.paid_rent = Number.isFinite(parsed) ? parsed : null;
    }
    addressData.rent_amount = String(asPayloadField("rent_amount") ?? "");
    // Applicant FamilyForm parity: Contact Relationship on FAMILY for current and previous.
    // Current RENT still carries the field (empty) for Angular payload shape.
    if (addressType === "FAMILY" || isCurrentAddress) {
      addressData.landlord_relationship_type = String(
        asPayloadField("landlord_relationship_type") ?? "",
      );
    }
    if (isCurrentAddress) {
      addressData.moveOut_date = String(asPayloadField("moveOut_date") ?? "");
      addressData.currently_living =
        (addressForm.currently_living ?? existingAddressData.currently_living)
          ? "1"
          : "0";
    }
  }

  return {
    ra_id: addressForm.ra_id || "",
    street_address: addressForm.street_address || "",
    outside_us: addressForm.outside_us || false,
    house_no: addressForm.house_no || "",
    street: addressForm.street || "",
    street_type: "",
    apartment: addressForm.apartment || "",
    city: addressForm.city || "",
    state: addressForm.state || "",
    zip_code: addressForm.zip_code || "",
    country: addressForm.country || "US",
    address_type: addressType,
    address_data: addressData,
  } satisfies AddressLike;
}

export function buildAboutYourselfFormChangePayload(
  personalInfo: AboutYourselfPersonalFormData & { non_us_address?: string },
  currentAddr: PreviousAddressRow | null,
  previousAddrs: PreviousAddressRow[],
  alternateAddrs: AboutYourselfAlternateAddressFormData[],
  deleted: {
    previous_address?: ReadonlyArray<number | string>;
    alternate_address?: ReadonlyArray<number | string>;
  } = {},
): AboutYourselfFormChangePayload {
  const allData: AboutYourselfFormChangePayload = { ...personalInfo };

  if (currentAddr) {
    const currentPayload = formatAboutYourselfAddressForApi(currentAddr, true);
    if (currentPayload) {
      allData.current_address = currentPayload;
    }
    const safePrevious = normalizePreviousAddressRows(previousAddrs);
    if (safePrevious.length > 0) {
      allData.previous_address = safePrevious
        .map((addr) => formatAboutYourselfAddressForApi(addr, false))
        .filter((addr): addr is AddressLike => addr != null);
    } else {
      allData.previous_address = [];
    }
  } else {
    const safePrevious = normalizePreviousAddressRows(previousAddrs);
    if (safePrevious.length > 0) {
      allData.previous_address = safePrevious
        .map((addr) => formatAboutYourselfAddressForApi(addr, false))
        .filter((addr): addr is AddressLike => addr != null);
    }
  }

  // Always emit alternate_address (even empty) so removing the last row is honored (Angular FormArray parity).
  allData.alternate_address = alternateAddrs.map((addr) => ({
    ra_id: addr.ra_id || "",
    street_address: addr.street_address || "",
    house_no: addr.house_no || "",
    street: addr.street || "",
    street_type: "",
    apartment: addr.apartment || "",
    city: addr.city || "",
    state: addr.state || "",
    zip_code: addr.zip_code || "",
    country: addr.country || "US",
    outside_us: addr.outside_us || false,
  }));

  // Angular `getRequestBody` deleted block — backend deletes by ra_id, not array omission alone.
  const deletedPrevious = deleted.previous_address ?? [];
  const deletedAlternate = deleted.alternate_address ?? [];
  if (deletedPrevious.length > 0 || deletedAlternate.length > 0) {
    allData.deleted = {
      ...(deletedPrevious.length > 0
        ? { previous_address: [...deletedPrevious] }
        : {}),
      ...(deletedAlternate.length > 0
        ? { alternate_address: [...deletedAlternate] }
        : {}),
    };
  }

  if (personalInfo.non_us_address !== undefined) {
    allData.non_us_address = personalInfo.non_us_address;
  }

  return allData;
}

import { ABOUT_YOURSELF_REVEAL_INLINE_ERRORS_EVENT } from '../../constants';

export function dispatchAboutYourselfRevealInlineErrors(): void {
  window.dispatchEvent(new CustomEvent(ABOUT_YOURSELF_REVEAL_INLINE_ERRORS_EVENT));
}
